import { useEffect, useState } from "react";
import { Loader2, Save, Check, AlertTriangle, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { adminApi, mediaRaw } from "./adminApi";
import { Field, MediaPicker } from "./components";
import { DynIcon, ICON_NAMES } from "@/lib/icons";

function useSetting(key) {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.getSetting(key).then((r) => setValue(r.value || {})); }, [key]);
  const save = async () => { setSaving(true); try { await adminApi.updateSetting(key, value); toast.success("Saved & published"); } catch (e) { toast.error("Save failed"); } finally { setSaving(false); } };
  return { value, setValue, save, saving };
}

function Txt({ label, v, onChange, ...p }) {
  return <Field label={label}><Input value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-[#050b18] border-slate-700" {...p} /></Field>;
}

function SaveBar({ onSave, saving, testId }) {
  return <div className="mt-6"><Button onClick={onSave} disabled={saving} className="cyan-grad text-[#041021] font-bold" data-testid={testId}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save & Publish</>}</Button></div>;
}

function Panel({ children }) { return <div className="max-w-2xl rounded-2xl glass p-6 space-y-4">{children}</div>; }

function BrandingTab() {
  const { value: b, setValue, save, saving } = useSetting("branding");
  const [picker, setPicker] = useState(null);
  if (!b) return <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />;
  const set = (k, v) => setValue({ ...b, [k]: v });
  const setNested = (p, k, v) => setValue({ ...b, [p]: { ...(b[p] || {}), [k]: v } });
  return (
    <Panel>
      <Txt label="Headline Line 1" v={b.headline_line1} onChange={(v) => set("headline_line1", v)} />
      <Txt label="Headline Line 2" v={b.headline_line2} onChange={(v) => set("headline_line2", v)} />
      <Txt label="Subtitle" v={b.subtitle} onChange={(v) => set("subtitle", v)} />
      <Txt label="Intro Text (Solutions screen)" v={b.intro_text} onChange={(v) => set("intro_text", v)} />
      <Txt label="CTA Label" v={b.cta_label} onChange={(v) => set("cta_label", v)} />
      <Txt label="Footer Text" v={b.footer_text} onChange={(v) => set("footer_text", v)} />
      <Field label="Hero Image">
        <div className="flex items-center gap-3">
          {b.hero_image_url && <img src={b.hero_image_url} alt="" className="h-16 w-24 rounded-lg object-cover" />}
          <Button type="button" variant="outline" className="border-slate-700" onClick={() => setPicker("hero")}>Choose</Button>
        </div>
      </Field>
      <Txt label="BSNL Logo URL" v={b.bsnl_logo_url} onChange={(v) => set("bsnl_logo_url", v)} />
      <div className="border-t border-slate-800 pt-4"><p className="mb-2 font-head font-semibold text-cyan-200">Thank You Screen</p>
        <Txt label="Title" v={b.thankyou?.title} onChange={(v) => setNested("thankyou", "title", v)} />
        <Txt label="Message" v={b.thankyou?.message} onChange={(v) => setNested("thankyou", "message", v)} />
        <Txt label="Button Label" v={b.thankyou?.button_label} onChange={(v) => setNested("thankyou", "button_label", v)} />
      </div>
      <div className="border-t border-slate-800 pt-4"><p className="mb-2 font-head font-semibold text-cyan-200">Closing Screen</p>
        <Txt label="Headline" v={b.closing?.headline} onChange={(v) => setNested("closing", "headline", v)} />
        <Field label="Closing Image"><div className="flex items-center gap-3">{b.closing?.image_url && <img src={b.closing.image_url} alt="" className="h-16 w-24 rounded-lg object-cover" />}<Button type="button" variant="outline" className="border-slate-700" onClick={() => setPicker("closing")}>Choose</Button></div></Field>
      </div>
      <SaveBar onSave={save} saving={saving} testId="save-branding" />
      {picker && <MediaPicker open kind="image" onClose={() => setPicker(null)} onSelect={(m) => { if (picker === "hero") set("hero_image_url", mediaRaw(m.id)); else setNested("closing", "image_url", mediaRaw(m.id)); setPicker(null); }} />}
    </Panel>
  );
}

function ContactTab() {
  const { value: c, setValue, save, saving } = useSetting("contact");
  if (!c) return <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />;
  const set = (k, v) => setValue({ ...c, [k]: v, is_sample: false });
  return (
    <Panel>
      <Txt label="Company Name" v={c.company_name} onChange={(v) => set("company_name", v)} />
      <Txt label="Headline" v={c.headline} onChange={(v) => set("headline", v)} />
      <Txt label="Message" v={c.message} onChange={(v) => set("message", v)} />
      <Txt label="Phone" v={c.phone} onChange={(v) => set("phone", v)} />
      <Txt label="Email" v={c.email} onChange={(v) => set("email", v)} />
      <Txt label="Website" v={c.website} onChange={(v) => set("website", v)} />
      <Txt label="Address" v={c.address} onChange={(v) => set("address", v)} />
      <Txt label="QR Destination URL" v={c.qr_url} onChange={(v) => set("qr_url", v)} />
      <SaveBar onSave={save} saving={saving} testId="save-contact" />
    </Panel>
  );
}

function KioskTab() {
  const { value: k, setValue, save, saving } = useSetting("kiosk");
  if (!k) return <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />;
  const set = (key, v) => setValue({ ...k, [key]: v });
  const Toggle = ({ label, field }) => (
    <label className="flex items-center justify-between rounded-lg bg-[#050b18] px-4 py-3">
      <span className="text-sm text-slate-300">{label}</span>
      <input type="checkbox" checked={!!k[field]} onChange={(e) => set(field, e.target.checked)} className="h-5 w-5 accent-cyan-500" data-testid={`kiosk-${field}`} />
    </label>
  );
  return (
    <Panel>
      <Txt label="Idle Timeout (seconds)" type="number" v={k.idle_timeout_seconds} onChange={(v) => set("idle_timeout_seconds", parseInt(v) || 0)} />
      <Txt label="Countdown Duration (seconds)" type="number" v={k.countdown_seconds} onChange={(v) => set("countdown_seconds", parseInt(v) || 0)} />
      <Toggle label="Auto-reset to welcome on idle" field="auto_reset_enabled" />
      <Toggle label="Attract / closing loop enabled" field="attract_enabled" />
      <Toggle label="Sound on by default" field="sound_default" />
      <Toggle label="Maintenance mode" field="maintenance_mode" />
      <Txt label="Maintenance Message" v={k.maintenance_message} onChange={(v) => set("maintenance_message", v)} />
      <SaveBar onSave={save} saving={saving} testId="save-kiosk" />
    </Panel>
  );
}

function JourneyTab() {
  const { value: j, setValue, save, saving } = useSetting("journey");
  const [picker, setPicker] = useState(null);
  if (!j) return <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />;
  const set = (k, v) => setValue({ ...j, [k]: v });
  const stages = j.stages || [];
  const setStage = (i, k, v) => { const arr = [...stages]; arr[i] = { ...arr[i], [k]: v }; set("stages", arr); };
  return (
    <Panel>
      <Txt label="Title" v={j.title} onChange={(v) => set("title", v)} />
      <Txt label="Subtitle" v={j.subtitle} onChange={(v) => set("subtitle", v)} />
      <Field label="Map Image"><div className="flex items-center gap-3">{j.map_image_url && <img src={j.map_image_url} alt="" className="h-16 w-24 rounded-lg object-cover" />}<Button variant="outline" className="border-slate-700" onClick={() => setPicker("map")}>Choose</Button></div></Field>
      <div className="space-y-3">
        <p className="font-head font-semibold text-cyan-200">Journey Stages</p>
        {stages.map((st, i) => (
          <div key={i} className="rounded-lg bg-[#050b18] p-3 space-y-2">
            <div className="flex gap-2">
              <Input value={st.title} onChange={(e) => setStage(i, "title", e.target.value)} placeholder="Title" className="bg-[#0b1730] border-slate-700" />
              <select value={st.icon} onChange={(e) => setStage(i, "icon", e.target.value)} className="rounded-lg border border-slate-700 bg-[#0b1730] px-2 text-sm">{ICON_NAMES.map((n) => <option key={n}>{n}</option>)}</select>
              <button onClick={() => set("stages", stages.filter((_, j2) => j2 !== i))} className="text-rose-300"><X className="h-4 w-4" /></button>
            </div>
            <Input value={st.description} onChange={(e) => setStage(i, "description", e.target.value)} placeholder="Description" className="bg-[#0b1730] border-slate-700" />
          </div>
        ))}
        <Button variant="outline" className="border-slate-700" onClick={() => set("stages", [...stages, { title: "New Stage", icon: "MapPin", description: "" }])}><Plus className="h-4 w-4" /> Add Stage</Button>
      </div>
      <SaveBar onSave={save} saving={saving} testId="save-journey" />
      {picker && <MediaPicker open kind="image" onClose={() => setPicker(null)} onSelect={(m) => { set("map_image_url", mediaRaw(m.id)); setPicker(null); }} />}
    </Panel>
  );
}

function EmailTab() {
  const { value: e, setValue, save, saving } = useSetting("email");
  const [configured, setConfigured] = useState(null);
  useEffect(() => { adminApi.emailStatus().then((r) => setConfigured(r.configured)); }, []);
  if (!e) return <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />;
  const set = (k, v) => setValue({ ...e, [k]: v });
  return (
    <Panel>
      <div className={`flex items-center gap-2 rounded-lg px-4 py-3 ${configured ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`} data-testid="email-provider-status">
        {configured ? <Check className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
        {configured === null ? "Checking provider…" : configured ? "Email provider is configured and ready." : "Email provider is NOT configured. Document delivery is disabled."}
      </div>
      <Txt label="Sender Display Name" v={e.sender_name} onChange={(v) => set("sender_name", v)} />
      <Txt label="Subject Template" v={e.subject_template} onChange={(v) => set("subject_template", v)} />
      <Txt label="Privacy Text" v={e.privacy_text} onChange={(v) => set("privacy_text", v)} />
      <Txt label="Retention (days)" type="number" v={e.retention_days} onChange={(v) => set("retention_days", parseInt(v) || 0)} />
      <SaveBar onSave={save} saving={saving} testId="save-email" />
    </Panel>
  );
}

export default function SettingsAdmin() {
  return (
    <div data-testid="admin-kiosk-settings">
      <h1 className="font-head text-3xl font-extrabold text-white">Settings</h1>
      <p className="mt-1 text-slate-400">Branding, contact, kiosk behaviour, journey and email.</p>
      <Tabs defaultValue="branding" className="mt-6">
        <TabsList className="flex-wrap bg-[#0b1730]">
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
          <TabsTrigger value="kiosk" data-testid="tab-kiosk">Kiosk</TabsTrigger>
          <TabsTrigger value="journey" data-testid="tab-journey">Journey</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
        </TabsList>
        <TabsContent value="branding" className="mt-4"><BrandingTab /></TabsContent>
        <TabsContent value="contact" className="mt-4"><ContactTab /></TabsContent>
        <TabsContent value="kiosk" className="mt-4"><KioskTab /></TabsContent>
        <TabsContent value="journey" className="mt-4"><JourneyTab /></TabsContent>
        <TabsContent value="email" className="mt-4"><EmailTab /></TabsContent>
      </Tabs>
    </div>
  );
}
